import React, { useState, useEffect } from 'react';
import { Phone, Mail, ArrowRight, Check, X, AlertCircle, Plus, Loader, Building, Lock, Shield } from 'lucide-react';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  onSnapshot,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// 🏢 MULTI-TENANT FIREBASE INQUIRY HANDLER
const InquiryHandler = ({ 
  onAddReservation, 
  inquiries, 
  onSaveInquiry,
  // Multi-tenant props
  currentUser,
  currentCompany,
  tenantContext,
  dataIsolationActive,
  validateCompanyAccess,
  getCompanyContext
}) => {
  const [localInquiries, setLocalInquiries] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [currentInquiry, setCurrentInquiry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [multiTenantReady, setMultiTenantReady] = useState(false);
  const [securityWarnings, setSecurityWarnings] = useState([]);
  const [inquiryStats, setInquiryStats] = useState({
    total: 0,
    whatsapp: 0,
    email: 0,
    converted: 0,
    pending: 0,
    companyName: ''
  });
  
  const [reservationData, setReservationData] = useState({
    customerName: '',
    whatsappNumber: '',
    localNumber: '',
    email: '',
    licenseNumber: '',
    pickupDateTime: '',
    dropoffDateTime: '',
    pickupLocation: 'SLBR',
    customPickupLocation: '',
    dropoffLocation: 'SLBR',
    customDropoffLocation: '',
    motorbikeNumber: '',
    availabilityStatus: 'pending',
    rentalAmount: '',
    rentalPaymentStatus: 'not paid',
    depositAmount: '$100',
    drivingLicenseStatus: 'not applied',
    drivingLicensePaymentStatus: 'not paid',
    licensePickupDate: '',
    licensePickupLocation: 'SLBR',
    customLicensePickupLocation: '',
    note: '',
    inquirySource: '',
    inquiryDate: '',
    isInquiry: true,
    // 🔒 Multi-tenant fields
    companyId: '',
    companyName: '',
    createdBy: '',
    createdByUsername: ''
  });

  // 🏢 COMPANY-SPECIFIC FIRESTORE COLLECTION
  const getCompanyCollectionPath = () => {
    const companyId = getCompanyContext();
    if (!companyId) {
      console.error('❌ No company context available for inquiries collection');
      return null;
    }
    return `companies/${companyId}/inquiries`;
  };

  const getInquiriesCollection = () => {
    const path = getCompanyCollectionPath();
    return path ? collection(db, path) : null;
  };

  // 🔒 MULTI-TENANT INITIALIZATION
  useEffect(() => {
    const initializeMultiTenantInquiries = () => {
      const warnings = [];
      
      // Validate multi-tenant context
      if (!currentUser || !currentCompany) {
        warnings.push('Missing user or company context - multi-tenant features unavailable');
        setMultiTenantReady(false);
        setSecurityWarnings(warnings);
        return;
      }

      // Validate user-company relationship
      if (currentUser.companyId !== currentCompany.id) {
        warnings.push('User-company relationship mismatch detected');
        setMultiTenantReady(false);
        setSecurityWarnings(warnings);
        return;
      }

      // Validate data isolation
      if (!dataIsolationActive) {
        warnings.push('Data isolation not active - security risk');
      }

      // Validate Firebase collection access
      const inquiriesCol = getInquiriesCollection();
      if (!inquiriesCol) {
        warnings.push('Cannot access company-specific inquiries collection');
        setMultiTenantReady(false);
        setSecurityWarnings(warnings);
        return;
      }

      setMultiTenantReady(true);
      setSecurityWarnings(warnings);
      
      console.log('✅ Multi-tenant inquiry handler initialized for company:', currentCompany.name);
      console.log('🔒 Data isolation active for company:', currentCompany.id);
    };

    initializeMultiTenantInquiries();
  }, [currentUser, currentCompany, dataIsolationActive, getCompanyContext]);

  // 👤 GET CURRENT USER ID WITH TENANT CONTEXT
  const getUserId = () => {
    if (!multiTenantReady) {
      console.warn('⚠️ Multi-tenant not ready, using fallback user ID');
      return 'fallback_user';
    }
    
    return currentUser?.id || tenantContext?.userId || 'demo_user';
  };

  // 📊 UPDATE INQUIRY STATISTICS
  const updateInquiryStats = (inquiriesList) => {
    if (!multiTenantReady) return;
    
    const stats = {
      total: inquiriesList.length,
      whatsapp: inquiriesList.filter(inq => inq.source === 'whatsapp').length,
      email: inquiriesList.filter(inq => inq.source === 'email').length,
      converted: inquiriesList.filter(inq => inq.converted).length,
      pending: inquiriesList.filter(inq => !inq.converted && !inq.dismissed).length,
      companyName: currentCompany?.name || 'Unknown'
    };
    
    setInquiryStats(stats);
  };

  // 🔒 USE COMPANY-SPECIFIC INQUIRIES WITH SECURITY VALIDATION
  useEffect(() => {
    if (!multiTenantReady) {
      setLocalInquiries([]);
      return;
    }

    if (inquiries) {
      // Security: Filter inquiries to only show current company's data
      const companyInquiries = inquiries.filter(inquiry => {
        // Allow legacy inquiries without companyId (with warning)
        if (!inquiry.companyId) {
          console.warn('⚠️ Found legacy inquiry without company ID:', inquiry.id);
          return false; // Exclude for security
        }
        
        // Only include inquiries for current company
        if (inquiry.companyId !== currentCompany.id) {
          console.warn('🚫 Filtered out inquiry from different company:', inquiry.companyId);
          return false;
        }
        
        return true;
      });
      
      setLocalInquiries(companyInquiries);
      updateInquiryStats(companyInquiries);
      
      console.log('📋 Loaded', companyInquiries.length, 'company-specific inquiries for:', currentCompany.name);
    }
  }, [inquiries, multiTenantReady, currentCompany]);

  // 🔥 FIREBASE SAVE WITH MULTI-TENANT ISOLATION
  const saveInquiryToFirebase = async (inquiryData) => {
    try {
      if (!multiTenantReady) {
        throw new Error('Multi-tenant context not ready');
      }

      setIsSaving(true);
      
      const inquiriesCollection = getInquiriesCollection();
      if (!inquiriesCollection) {
        throw new Error('Cannot access company inquiries collection');
      }

      const dataToSave = {
        ...inquiryData,
        userId: getUserId(),
        createdAt: serverTimestamp(),
        converted: false,
        dismissed: false,
        // 🔒 MULTI-TENANT ISOLATION FIELDS
        companyId: currentCompany.id,
        companyName: currentCompany.name,
        createdBy: currentUser.id,
        createdByUsername: currentUser.username,
        tenantIsolated: true,
        // Audit trail
        createdByRole: currentUser.role,
        createdFromComponent: 'InquiryHandler'
      };
      
      console.log('💾 Saving inquiry to company collection:', currentCompany.name);
      const docRef = await addDoc(inquiriesCollection, dataToSave);
      
      console.log('✅ Inquiry saved with multi-tenant isolation for company:', currentCompany.name);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error saving company inquiry:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // 🔄 FIREBASE UPDATE WITH COMPANY VALIDATION
  const updateInquiryStatus = async (inquiryId, status) => {
    try {
      if (!multiTenantReady) {
        throw new Error('Multi-tenant context not ready');
      }

      setIsSaving(true);
      
      // Validate inquiry belongs to current company
      const inquiry = localInquiries.find(inq => inq.id === inquiryId);
      if (!inquiry) {
        throw new Error('Inquiry not found');
      }
      
      if (inquiry.companyId && inquiry.companyId !== currentCompany.id) {
        throw new Error('Security violation: Inquiry belongs to different company');
      }

      const inquiryRef = doc(db, getCompanyCollectionPath(), inquiryId);
      await updateDoc(inquiryRef, {
        [status]: true,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.id,
        updatedByUsername: currentUser.username,
        updatedByRole: currentUser.role
      });
      
      console.log('✅ Inquiry status updated with company validation:', currentCompany.name);
    } catch (error) {
      console.error('❌ Error updating company inquiry:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };
  
  // 📱 HANDLE NEW INQUIRY WITH MULTI-TENANT CONTEXT
  const handleNewInquiry = async (inquiry) => {
    try {
      if (!multiTenantReady) {
        alert('Multi-tenant system not ready. Please try again.');
        return;
      }

      const inquiryId = await saveInquiryToFirebase(inquiry);
      
      // Dispatch company-specific event
      window.dispatchEvent(new CustomEvent('newInquiry', { 
        detail: { 
          ...inquiry, 
          id: inquiryId,
          companyId: currentCompany.id,
          companyName: currentCompany.name
        } 
      }));
      
      alert(`New inquiry added successfully for ${currentCompany.name}!`);
    } catch (error) {
      console.error('❌ Error adding company inquiry:', error);
      alert('Error adding inquiry. Please try again.');
    }
  };
  
  const handleOpenInquiry = (inquiry) => {
    if (!multiTenantReady) {
      alert('Multi-tenant system not ready. Please try again.');
      return;
    }

    // Validate inquiry belongs to current company
    if (!validateCompanyAccess(inquiry.companyId)) {
      alert('Access denied: This inquiry belongs to a different company.');
      return;
    }

    setCurrentInquiry(inquiry);
    
    // Pre-fill reservation data with multi-tenant context
    setReservationData({
      ...reservationData,
      customerName: inquiry.name || '',
      whatsappNumber: inquiry.source === 'whatsapp' ? inquiry.contact : '',
      email: inquiry.source === 'email' ? inquiry.contact : '',
      note: inquiry.message || '',
      inquirySource: inquiry.source,
      inquiryDate: inquiry.date,
      // Multi-tenant context
      companyId: currentCompany.id,
      companyName: currentCompany.name,
      createdBy: currentUser.id,
      createdByUsername: currentUser.username
    });
    
    setShowPopup(true);
  };
  
  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentInquiry(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReservationData((prevData) => ({ ...prevData, [name]: value }));
  };
  
  const handleConvertToReservation = async (e) => {
    e.preventDefault();
    
    if (!multiTenantReady) {
      alert('Multi-tenant system not ready. Please try again.');
      return;
    }
    
    // Validate required fields
    if (!reservationData.customerName || !reservationData.pickupDateTime || !reservationData.dropoffDateTime) {
      alert('Please fill in all required fields: Name, Pickup Date, and Dropoff Date');
      return;
    }
    
    try {
      // Ensure reservation has company context
      const reservationWithTenant = {
        ...reservationData,
        companyId: currentCompany.id,
        companyName: currentCompany.name,
        createdBy: currentUser.id,
        createdByUsername: currentUser.username
      };

      // Call the onAddReservation callback with tenant context
      await onAddReservation(reservationWithTenant);
      
      // Mark inquiry as converted with company validation
      if (currentInquiry && currentInquiry.id) {
        await updateInquiryStatus(currentInquiry.id, 'converted');
      }
      
      handleClosePopup();
      
      alert(`Inquiry successfully converted to reservation for ${currentCompany.name}!`);
      
    } catch (error) {
      console.error('❌ Error converting company inquiry:', error);
      alert('Error converting inquiry to reservation. Please try again.');
    }
  };
  
  const handleDismissInquiry = async (inquiry) => {
    try {
      if (!multiTenantReady) {
        alert('Multi-tenant system not ready. Please try again.');
        return;
      }

      // Validate inquiry belongs to current company
      if (!validateCompanyAccess(inquiry.companyId)) {
        alert('Access denied: This inquiry belongs to a different company.');
        return;
      }

      if (inquiry.id) {
        await updateInquiryStatus(inquiry.id, 'dismissed');
        alert(`Inquiry dismissed successfully for ${currentCompany.name}!`);
      }
    } catch (error) {
      console.error('❌ Error dismissing company inquiry:', error);
      alert('Error dismissing inquiry. Please try again.');
    }
  };
  
  // 🔒 COMPANY-SPECIFIC PENDING INQUIRIES WITH SECURITY
  const pendingInquiries = localInquiries.filter(inq => {
    // Additional security check
    if (inq.companyId && inq.companyId !== currentCompany?.id) {
      console.warn('🚫 Filtering out inquiry from different company in render');
      return false;
    }
    return !inq.converted && !inq.dismissed;
  });
  
  // 🧪 GENERATE TEST INQUIRY WITH COMPANY CONTEXT
  const generateTestInquiry = async () => {
    if (!multiTenantReady) {
      alert('Multi-tenant system not ready. Please try again.');
      return;
    }

    const testInquiry = {
      name: `${currentCompany.name} Test Customer ${Date.now()}`,
      contact: `+94 76 ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000) + 1000}`,
      message: `Test inquiry for ${currentCompany.name}. I would like to rent a motorcycle for 3 days. Could you please provide me with available options and pricing?`,
      date: new Date().toISOString(),
      source: Math.random() > 0.5 ? 'whatsapp' : 'email'
    };
    
    await handleNewInquiry(testInquiry);
  };

  // 🚫 PERMISSION CHECK
  if (!currentUser || !currentCompany) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 text-center">
        <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-300 mb-2">Access Denied</h3>
        <p className="text-gray-500">Multi-tenant context required for inquiry management.</p>
      </div>
    );
  }

  // 🔄 LOADING STATE
  if (!multiTenantReady) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-white">Initializing multi-tenant inquiry system...</p>
        <p className="text-gray-400 text-sm mt-2">Setting up secure data isolation for {currentCompany?.name}</p>
      </div>
    );
  }
  
  return (
    <div>
      {/* 🔒 MULTI-TENANT HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          New Booking Inquiries
          <div className="flex items-center text-green-400 text-xs">
            <Lock size={12} className="mr-1" />
            <span>Multi-Tenant</span>
          </div>
        </h3>
        <div className="flex items-center gap-3">
          <div className="text-right text-sm">
            <div className="text-blue-400 flex items-center">
              <Building size={12} className="mr-1" />
              {currentCompany.name}
            </div>
            <div className="text-gray-400">
              {pendingInquiries.length} pending • {inquiryStats.total} total
            </div>
          </div>
          {isSaving && (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader size={16} className="animate-spin" />
              <span className="text-sm">Processing...</span>
            </div>
          )}
          <button 
            className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600/50 text-white px-3 py-1 rounded-lg text-sm transition-colors flex items-center gap-1"
            onClick={generateTestInquiry}
            disabled={isSaving || !multiTenantReady}
          >
            <Plus size={16} /> Add Test Inquiry
          </button>
        </div>
      </div>

      {/* 🚨 SECURITY WARNINGS */}
      {securityWarnings.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 mb-4">
          <div className="flex items-center text-red-400 text-sm mb-2">
            <AlertCircle className="mr-2" size={16} />
            Multi-Tenant Security Warnings ({securityWarnings.length})
          </div>
          <ul className="list-disc list-inside text-red-300 text-xs space-y-1">
            {securityWarnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 📊 COMPANY STATISTICS */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700 rounded-lg p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-sm">
          <div>
            <div className="text-blue-300 font-medium">{inquiryStats.total}</div>
            <div className="text-blue-400 text-xs">Total</div>
          </div>
          <div>
            <div className="text-green-300 font-medium">{inquiryStats.whatsapp}</div>
            <div className="text-green-400 text-xs">WhatsApp</div>
          </div>
          <div>
            <div className="text-purple-300 font-medium">{inquiryStats.email}</div>
            <div className="text-purple-400 text-xs">Email</div>
          </div>
          <div>
            <div className="text-orange-300 font-medium">{inquiryStats.converted}</div>
            <div className="text-orange-400 text-xs">Converted</div>
          </div>
          <div>
            <div className="text-yellow-300 font-medium">{inquiryStats.pending}</div>
            <div className="text-yellow-400 text-xs">Pending</div>
          </div>
        </div>
      </div>
      
      {pendingInquiries.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 text-center text-gray-400">
          <AlertCircle size={40} className="mx-auto mb-3 text-gray-500" />
          <p>No pending inquiries for {currentCompany.name}</p>
          <p className="text-sm mt-2">New WhatsApp and email inquiries will appear here</p>
          <div className="mt-3 text-xs text-green-400 flex items-center justify-center">
            <Check size={12} className="mr-1" />
            Connected to Firebase • Real-time updates • Company isolated
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {pendingInquiries.map((inquiry) => (
            <div 
              key={inquiry.id} 
              className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-500 transition-colors"
            >
              <div className="flex justify-between">
                <div className="flex items-center mb-2">
                  {inquiry.source === 'whatsapp' ? (
                    <Phone size={18} className="text-green-500 mr-2" />
                  ) : (
                    <Mail size={18} className="text-blue-500 mr-2" />
                  )}
                  <span className="font-medium text-white">
                    {inquiry.source === 'whatsapp' ? 'WhatsApp' : 'Email'} Inquiry: {inquiry.name}
                  </span>
                  <div className="ml-3 flex items-center text-green-400 text-xs">
                    <Lock size={10} className="mr-1" />
                    <span>{currentCompany.name}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {inquiry.date ? new Date(inquiry.date).toLocaleString() : 'Unknown date'}
                </div>
              </div>
              
              <div className="bg-gray-750 p-3 rounded-md mb-3 text-gray-300 border-l-4 border-gray-600">
                <div className="mb-2">
                  <strong>Contact:</strong> {inquiry.contact}
                </div>
                <div>
                  <strong>Message:</strong> {inquiry.message}
                </div>
                {inquiry.companyId && (
                  <div className="mt-2 text-xs text-gray-500">
                    Company ID: {inquiry.companyId.slice(-8)}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  className="px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:bg-gray-600/50 rounded-lg flex items-center text-sm transition-colors"
                  onClick={() => handleDismissInquiry(inquiry)}
                  disabled={isSaving}
                >
                  <X size={14} className="mr-1" /> Dismiss
                </button>
                <button
                  className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-blue-600/50 disabled:to-indigo-700/50 text-white rounded-lg flex items-center text-sm transition-colors"
                  onClick={() => handleOpenInquiry(inquiry)}
                  disabled={isSaving}
                >
                  <ArrowRight size={14} className="mr-1" /> Convert to Reservation
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* 🔒 MULTI-TENANT CONVERT INQUIRY POPUP */}
      {showPopup && currentInquiry && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center">
                  {currentInquiry.source === 'whatsapp' ? (
                    <Phone size={20} className="text-green-500 mr-2" />
                  ) : (
                    <Mail size={20} className="text-blue-500 mr-2" />
                  )}
                  Convert Inquiry to Reservation
                  <div className="ml-3 flex items-center text-green-400 text-sm">
                    <Lock size={14} className="mr-1" />
                    <span>Multi-Tenant</span>
                  </div>
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  From {currentInquiry.name} via {currentInquiry.source} • Company: {currentCompany.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">Firebase ID: {currentInquiry.id}</p>
              </div>
              <button 
                onClick={handleClosePopup} 
                className="text-gray-400 hover:text-white"
                disabled={isSaving}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="bg-gray-750 p-4 rounded-md mb-6 border-l-4 border-gray-600">
              <div className="text-xs text-gray-400 mb-1">Original Inquiry:</div>
              <div className="text-gray-300">{currentInquiry.message}</div>
            </div>

            <form onSubmit={handleConvertToReservation} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    name="customerName"
                    value={reservationData.customerName}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter customer name"
                    required
                    disabled={isSaving}
                  />
                </div>

                {/* WhatsApp Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">WhatsApp Number</label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    value={reservationData.whatsappNumber}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter WhatsApp number"
                    disabled={isSaving}
                  />
                </div>

                {/* Local Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Local Number</label>
                  <input
                    type="text"
                    name="localNumber"
                    value={reservationData.localNumber}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter local contact number"
                    disabled={isSaving}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={reservationData.email}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter email address"
                    disabled={isSaving}
                  />
                </div>

                {/* Pickup Date & Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Pickup Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="pickupDateTime"
                    value={reservationData.pickupDateTime}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    required
                    disabled={isSaving}
                  />
                </div>
                
                {/* Drop-off Date & Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Drop-off Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="dropoffDateTime"
                    value={reservationData.dropoffDateTime}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    required
                    disabled={isSaving}
                  />
                </div>

                {/* Motorbike Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Motorbike</label>
                  <select
                    name="motorbikeNumber"
                    value={reservationData.motorbikeNumber}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    disabled={isSaving}
                  >
                    <option value="">Select Motorbike</option>
                    <option value="DIO 123">DIO 123</option>
                    <option value="APRILIA XYZ">APRILIA XYZ</option>
                    <option value="XPULSE 456">XPULSE 456</option>
                    <option value="XBLADE 789">XBLADE 789</option>
                  </select>
                </div>
                
                {/* Availability Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                  <select
                    name="availabilityStatus"
                    value={reservationData.availabilityStatus}
                    onChange={handleInputChange}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    disabled={isSaving}
                  >
                    <option value="pending">Pending</option>
                    <option value="booked">Booked</option>
                    <option value="cancel">Cancel</option>
                  </select>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Note</label>
                <textarea
                  name="note"
                  value={reservationData.note}
                  onChange={handleInputChange}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 disabled:opacity-50"
                  placeholder="Enter any notes"
                  disabled={isSaving}
                ></textarea>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={handleClosePopup}
                  className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-blue-600/50 disabled:to-indigo-700/50 text-white rounded-lg transition-colors flex items-center gap-2"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      <span>Create Reservation</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔒 MULTI-TENANT STATUS FOOTER */}
      {multiTenantReady && dataIsolationActive && (
        <div className="mt-4 bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-500/30 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center space-x-4 text-sm">
            <div className="flex items-center text-green-400">
              <Check size={16} className="mr-2" />
              <span>Multi-tenant active</span>
            </div>
            <div className="flex items-center text-blue-400">
              <Lock size={16} className="mr-2" />
              <span>Data isolated to {currentCompany.name}</span>
            </div>
            <div className="flex items-center text-purple-400">
              <Building size={16} className="mr-2" />
              <span>{inquiryStats.total} company inquiries</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryHandler;